import React, { useMemo, useState } from "react";
import {
  generateCumulativeTimeSeries,
  getColor,
  getPast12Months,
} from "../../utils/helpers";
import styles from "./GraphAccordion.module.scss";
import { Tooltip } from "react-tooltip";
import { useSelector } from "react-redux";
import LineGraph from "../LineGraph/LineGraph";
import DownArrowIcon, {
  ReactComponent as DownArrow,
} from "../../icons/down-arrow.svg";
import UpArrowIcon, {
  ReactComponent as UpArrow,
} from "../../icons/up-arrow.svg";
import { ReactComponent as InfoCircle } from "../../icons/info-circle.svg";
import { ReactComponent as SortAscIcon } from "../../icons/sort-ascending.svg";
import { ReactComponent as SortDescIcon } from "../../icons/sort-descending.svg";
import { ReactComponent as SortIcon } from "../../icons/sort-default.svg";
import { ReactComponent as RightArrowIcon } from "../../icons/right-arrow.svg";
import classNames from "classnames";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../Button/Button";

const GraphAccordion = ({
  findingsData,
  auditsTasksData,
  improvementsData,
  meModeActive = false,
  currentUser,
}) => {
  const { orgId } = useParams();
  const navigate = useNavigate();

  const [openGroups, setOpenGroups] = useState([]);
  const [sortMode, setSortMode] = useState("id-asc"); // Default sort by ID ascending
  const [idSortMode, setIdSortMode] = useState("asc"); // ID sort state
  const [rpsSortMode, setRpsSortMode] = useState(null); // RPS sort state

  const { risksData } = useSelector((state) => state.organization);

  // Get past 12 months for headers
  const months = getPast12Months();

  const [startYear, startMonth] = months[0].split("-");
  const startDate = new Date(parseInt(startYear), parseInt(startMonth) - 1, 1);

  const [endYear, endMonth] = months[months.length - 1].split("-");
  const endDate = new Date(
    parseInt(endYear),
    parseInt(endMonth),
    0, // last day of the previous month (e.g., "2024-07" gives July 31)
  );

  // Helper function to check if a finding is relevant for the current user in me mode
  const isFindingRelevantForUser = (finding) => {
    if (!meModeActive) return true;

    return [
      finding.responsible,
      finding.accountable,
      finding.consulted,
      finding.informed,
      finding.reporter,
    ].some((role) =>
      Array.isArray(role) ? role.includes(currentUser) : role === currentUser,
    );
  };

  // Helper function to check if an improvement is relevant for the current user in me mode
  const isImprovementRelevantForUser = (improvement) => {
    if (!meModeActive) return true;

    // Check if the improvement is assigned to the current user
    // You may need to adjust these field names based on your improvement data structure
    return [
      improvement.responsible,
      improvement.accountable,
      improvement.consulted,
      improvement.informed,
    ].some((role) =>
      Array.isArray(role) ? role.includes(currentUser) : role === currentUser,
    );
  };

  // Helper function to check if an audit is relevant for the current user in me mode
  const isAuditRelevantForUser = (auditTask) => {
    if (!meModeActive) return true;

    // Check if any linked findings are relevant to the user
    return auditTask.linked_findings.some((findingId) => {
      const finding = findingsData[findingId];
      if (!finding) return false;
      return isFindingRelevantForUser(finding);
    });
  };

  const normalizeDate = (str) => {
    const [y, m, d] = str.split("-");
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  };

  // Filter data based on me mode and date range
  const filteredAuditsTasksArray = Object.values(auditsTasksData || {}).filter(
    (audit) => {
      if (audit.archived) return false;
      const isInDateRange =
        normalizeDate(audit.date) >= startDate &&
        normalizeDate(audit.date) <= endDate;
      return isInDateRange && isAuditRelevantForUser(audit);
    },
  );

  const filteredFindingsArray = Object.values(findingsData || {}).filter(
    (finding) => {
      if (finding.archived) return false;
      const isInDateRange =
        normalizeDate(finding.date) >= startDate &&
        normalizeDate(finding.date) <= endDate;
      return isInDateRange && isFindingRelevantForUser(finding);
    },
  );

  const filteredImprovementsArray = Object.values(
    improvementsData || {},
  ).filter((improvement) => {
    if (improvement.archived) return false;
    const isInDateRange =
      normalizeDate(improvement.date) >= startDate &&
      normalizeDate(improvement.date) <= endDate;
    return isInDateRange && isImprovementRelevantForUser(improvement);
  });

  const { chapters, globalMin, globalMax, chapterCounts } = useMemo(() => {
    const isValidFinding = (f) =>
      f?.risk && f.risk.level && f.risk.level.toLowerCase() !== "none";

    // Use filtered findings array
    const validFindingsArray = filteredFindingsArray.filter(isValidFinding);
    const validFindingsMap = Object.fromEntries(
      validFindingsArray.map((f) => [f.key, f]),
    );

    // Use filtered improvements array
    const filteredImprovementsMap = Object.fromEntries(
      filteredImprovementsArray
        .filter((i) => !i.archived)
        .map((i) => [i.key, i]),
    );

    const groupedData = generateCumulativeTimeSeries(
      validFindingsMap,
      filteredImprovementsMap,
    );

    const chapters = {};
    let globalMin = Infinity;
    let globalMax = -Infinity;

    const auditCountByChapter = {};
    const findingCountByChapter = {};
    const improvementCountByChapter = {};
    const exposureByChapter = {};
    const rpsByChapter = {};

    const auditCountBySubchapter = {};
    const findingCountBySubchapter = {};
    const improvementCountBySubchapter = {};
    const exposureBySubchapter = {};
    const rpsBySubchapter = {};

    Object.keys(groupedData).forEach((subChapter) => {
      const mainChapter = subChapter.split("-")[0];

      if (!chapters[mainChapter]) {
        chapters[mainChapter] = {
          subChapters: [],
          cumulativeData: groupedData[subChapter].map((monthData) => ({
            ...monthData,
            count: 0,
          })),
        };
      }

      groupedData[subChapter].forEach((monthData, index) => {
        const count = monthData.count;
        chapters[mainChapter].cumulativeData[index].count += count;
        if (count < globalMin) globalMin = count;
        if (count > globalMax) globalMax = count;
      });

      const matchingRisk = Object.values(risksData).find(
        (r) => r.risk_id === subChapter,
      );

      const subchapterName =
        Object.values(risksData).find((r) => r.risk_id === subChapter)
          ?.description || "";

      const riskLevel = matchingRisk?.level?.toLowerCase() || "none";

      chapters[mainChapter].subChapters.push({
        key: subChapter,
        name: subchapterName,
        level: riskLevel,
        data: groupedData[subChapter],
      });
    });

    // Count audits by chapter/subchapter using filtered data
    const auditKeysByChapter = {};
    filteredAuditsTasksArray.forEach((audit) => {
      audit.form?.forEach((formItem) => {
        const riskId = formItem?.risk?.risk_id;
        const level = formItem?.risk?.level;
        if (level?.toLowerCase() === "none") return;
        const mainChapter = riskId?.split("-")[0];
        if (riskId) {
          auditCountBySubchapter[riskId] =
            (auditCountBySubchapter[riskId] || 0) + 1;
        }
        if (mainChapter) {
          if (!auditKeysByChapter[mainChapter]) {
            auditKeysByChapter[mainChapter] = new Set();
          }
          auditKeysByChapter[mainChapter].add(audit.key);
        }
      });
    });

    Object.entries(auditKeysByChapter).forEach(([chapter, auditKeys]) => {
      auditCountByChapter[chapter] = auditKeys.size;
    });

    // Calculate RPS using filtered data
    Object.entries(groupedData).forEach(([subChapterKey, monthlyData]) => {
      const mainChapter = subChapterKey.split("-")[0];
      const lastMonthCount = monthlyData[monthlyData.length - 1]?.count || 0;

      rpsBySubchapter[subChapterKey] = lastMonthCount;
      rpsByChapter[mainChapter] =
        (rpsByChapter[mainChapter] || 0) + lastMonthCount;
    });

    // Count findings and exposure using filtered data
    validFindingsArray.forEach((finding) => {
      const { risk } = finding;
      const qmsId = risk.qms_id;
      const riskId = risk.risk_id;
      const level = risk.level?.toLowerCase();
      const impact =
        level === "low" ? 1 : level === "medium" ? 2 : level === "high" ? 3 : 0;

      if (qmsId) {
        findingCountByChapter[qmsId] = (findingCountByChapter[qmsId] || 0) + 1;
        exposureByChapter[qmsId] = (exposureByChapter[qmsId] || 0) + impact;
      }

      if (riskId) {
        findingCountBySubchapter[riskId] =
          (findingCountBySubchapter[riskId] || 0) + 1;
        exposureBySubchapter[riskId] =
          (exposureBySubchapter[riskId] || 0) + impact;
      }
    });

    // Count improvements using filtered data
    filteredImprovementsArray.forEach((improvement) => {
      improvement.linked_findings?.forEach((findingKey) => {
        const finding = findingsData?.[findingKey];
        if (!isValidFinding(finding)) return;

        const { qms_id, risk_id } = finding.risk;
        if (qms_id) {
          improvementCountByChapter[qms_id] =
            (improvementCountByChapter[qms_id] || 0) + 1;
        }
        if (risk_id) {
          improvementCountBySubchapter[risk_id] =
            (improvementCountBySubchapter[risk_id] || 0) + 1;
        }
      });
    });

    return {
      chapters,
      globalMin,
      globalMax,
      chapterCounts: {
        auditCountByChapter,
        findingCountByChapter,
        improvementCountByChapter,
        exposureByChapter,
        rpsByChapter,
        auditCountBySubchapter,
        findingCountBySubchapter,
        improvementCountBySubchapter,
        exposureBySubchapter,
        rpsBySubchapter,
      },
    };
  }, [
    filteredFindingsArray,
    filteredAuditsTasksArray,
    filteredImprovementsArray,
    risksData,
  ]);

  const toggleAccordion = (mainChapterKey) => {
    if (openGroups.includes(mainChapterKey)) {
      setOpenGroups(openGroups.filter((key) => key !== mainChapterKey));
    } else {
      setOpenGroups([...openGroups, mainChapterKey]);
    }
  };

  // Function to filter out duplicate improvements based on their key
  const dedupeImprovements = (improvements) => {
    const seenKeys = new Set();
    return improvements.filter((improvement) => {
      if (seenKeys.has(improvement.key)) {
        return false;
      } else {
        seenKeys.add(improvement.key);
        return true;
      }
    });
  };

  const chapterKeys = Object.keys(chapters);

  // Natural sort function for IDs like A1, A2, B1, etc.
  const naturalSort = (a, b) => {
    const regex = /(\D+)(\d+)/;
    const aMatch = a.match(regex);
    const bMatch = b.match(regex);

    if (aMatch && bMatch) {
      const [, aLetter, aNumber] = aMatch;
      const [, bLetter, bNumber] = bMatch;

      if (aLetter === bLetter) {
        return parseInt(aNumber) - parseInt(bNumber);
      }
      return aLetter.localeCompare(bLetter);
    }

    return a.localeCompare(b);
  };

  const sortedChapterKeys = [...chapterKeys].sort((a, b) => {
    // Check which sort is active
    if (sortMode === "id-asc") {
      return naturalSort(a, b);
    }
    if (sortMode === "id-desc") {
      return naturalSort(b, a);
    }
    if (sortMode === "rps-asc") {
      return (
        (chapterCounts.rpsByChapter[a] || 0) -
        (chapterCounts.rpsByChapter[b] || 0)
      );
    }
    if (sortMode === "rps-desc") {
      return (
        (chapterCounts.rpsByChapter[b] || 0) -
        (chapterCounts.rpsByChapter[a] || 0)
      );
    }
    return naturalSort(a, b); // Default to ID ascending
  });

  const handleIdSort = () => {
    if (idSortMode === "asc") {
      setIdSortMode("desc");
      setSortMode("id-desc");
    } else if (idSortMode === "desc") {
      setIdSortMode("asc");
      setSortMode("id-asc");
    } else {
      // When clicking from inactive state, go to desc
      setIdSortMode("desc");
      setSortMode("id-desc");
    }
    setRpsSortMode(null); // Clear RPS sort
  };

  const handleRpsSort = () => {
    if (rpsSortMode === "desc") {
      setRpsSortMode("asc");
      setSortMode("rps-asc");
    } else if (rpsSortMode === "asc") {
      setRpsSortMode("desc");
      setSortMode("rps-desc");
    } else {
      // When clicking from inactive state, go to desc
      setRpsSortMode("desc");
      setSortMode("rps-desc");
    }
    setIdSortMode(null); // Clear ID sort
  };

  const truncateText = (text, maxLength) => {
    if (text?.length <= maxLength) {
      return text;
    }
    return (
      <>
        {text?.substring(0, maxLength)}
        <span>...</span>
      </>
    );
  };

  const getImprovementRiskLevelForSubchapter = (
    improvement,
    subChapterKey,
    findingsData,
  ) => {
    const linkedFindings = improvement.linked_findings || [];

    // Find a linked finding that belongs to the current subchapter
    for (const findingKey of linkedFindings) {
      const finding = findingsData?.[findingKey];
      if (finding?.risk?.risk_id === subChapterKey) {
        return finding.risk.level?.toLowerCase() || "none";
      }
    }
  };

  const getLatestStatus = (finding, month) => {
    let latestStatus = finding.status;
    for (const update of finding.updates || []) {
      const updateMonth = update.date?.slice(0, 7);
      if (updateMonth <= month && update.body.includes("Status:")) {
        const match = update.body.match(/Status: (\w+)/);
        if (match) latestStatus = match[1];
      }
    }
    return latestStatus;
  };

  return (
    <div className={styles.graphAccordion}>
      <div className={styles.accordionHeaderRow}>
        <div
          className={styles.columnAll}
          onClick={() => {
            const allChapterKeys = Object.keys(chapters);
            const areAllOpen = allChapterKeys.every((key) =>
              openGroups.includes(key),
            );

            if (areAllOpen) {
              setOpenGroups([]);
            } else {
              setOpenGroups(allChapterKeys);
            }
          }}
        >
          {openGroups.length === Object.keys(chapters).length ? (
            <UpArrow className={styles.toggleAllIcon} />
          ) : (
            <DownArrow className={styles.toggleAllIcon} />
          )}
          <span className={styles.toggleAllText}>ALL</span>
        </div>
        <div className={styles.columnId} onClick={handleIdSort}>
          {idSortMode === "asc" ? (
            <SortAscIcon className={styles.sortIcon} />
          ) : idSortMode === "desc" ? (
            <SortDescIcon className={styles.sortIcon} />
          ) : (
            <SortAscIcon className={styles.sortIcon} />
          )}
          ID
        </div>
        <div className={styles.columnName}>NAME</div>
        <div className={styles.columnAudits}>AUDITS</div>
        <div className={styles.columnFindings}>FINDINGS</div>
        <div className={styles.columnImprovements}>IMPROVEMENTS</div>
        <div className={styles.columnExposure}>
          EXPOSURE
          <InfoCircle
            className={styles.infoIcon}
            data-tooltip-id="exposure-tooltip"
          />
        </div>
        <div className={styles.columnRps} onClick={handleRpsSort}>
          {rpsSortMode === "asc" ? (
            <SortAscIcon className={styles.sortIcon} />
          ) : rpsSortMode === "desc" ? (
            <SortDescIcon className={styles.sortIcon} />
          ) : (
            <SortAscIcon className={styles.sortIcon} />
          )}
          PRIORITY SCORE
          <InfoCircle
            className={styles.infoIcon}
            data-tooltip-id="rps-tooltip"
          />
        </div>
      </div>
      {Object.keys(chapters).length === 0 ? (
        <div className={styles.noData}>
          {meModeActive
            ? "Sorry, there is no data to display for your assignments in the previous year."
            : "Sorry, there is no data to display for the previous year."}
        </div>
      ) : (
        sortedChapterKeys.map((mainChapterKey) => {
          const { subChapters, cumulativeData } = chapters[mainChapterKey];
          const {
            auditCountByChapter,
            findingCountByChapter,
            improvementCountByChapter,
            exposureByChapter,
            rpsByChapter,
            auditCountBySubchapter,
            findingCountBySubchapter,
            improvementCountBySubchapter,
            exposureBySubchapter,
            rpsBySubchapter,
          } = chapterCounts;

          // Finding the corresponding qms_description for the main chapter
          const matchingRisk = Object.values(risksData).find(
            (risk) => risk.qms_id === mainChapterKey,
          );
          const riskDescription = matchingRisk
            ? matchingRisk.qms_description
            : "";

          const min = globalMin;
          const max = globalMax;

          return (
            <div key={mainChapterKey} className={styles.accordionItem}>
              {/* Header Row with counts */}
              <div
                className={styles.accordionHeaderWrapper}
                onClick={() => toggleAccordion(mainChapterKey)}
              >
                <div className={styles.accordionHeader}>
                  <span className={styles.chapterKey}>{mainChapterKey}</span>
                  <span className={styles.description}>{riskDescription}</span>

                  <div className={styles.audits}>
                    {auditCountByChapter[mainChapterKey] || 0}
                  </div>
                  <div className={styles.findings}>
                    {findingCountByChapter[mainChapterKey] || 0}
                  </div>
                  <div className={styles.improvements}>
                    {improvementCountByChapter[mainChapterKey] || 0}
                  </div>
                  <div className={styles.exposure}>
                    <span className={styles.exposurePill}>
                      {exposureByChapter[mainChapterKey] || 0}
                    </span>
                  </div>
                  <div className={styles.rps}>
                    <span className={styles.rpsPill}>
                      {rpsByChapter[mainChapterKey] || 0}
                    </span>
                  </div>
                </div>

                <div className={styles.legendRow}>
                  {openGroups.includes(mainChapterKey) ? (
                    <img
                      src={UpArrowIcon}
                      alt="Collapse"
                      className={styles.icon}
                    />
                  ) : (
                    <img
                      src={DownArrowIcon}
                      alt="Expand"
                      className={styles.icon}
                    />
                  )}
                  <div className={styles.legend}>
                    {cumulativeData.map((item, index) => (
                      <div
                        key={index}
                        className={styles.monthSquare}
                        data-tooltip-id="month-tooltip"
                        data-tooltip-html={`${new Date(
                          months[index] + "-01",
                        ).toLocaleString("default", {
                          month: "long",
                        })} PRIORITY SCORE: ${item.count}`}
                        style={{
                          backgroundColor: getColor(item.count, min, max),
                        }}
                      >
                        {months[index].slice(5, 7) === "08"
                          ? "AUG"
                          : new Date(months[index] + "-01")
                              .toLocaleString("default", {
                                month: "short",
                              })
                              .toUpperCase()}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Accordion Body */}
              {openGroups.includes(mainChapterKey) && (
                <div className={styles.accordionBody}>
                  {subChapters.map(
                    ({
                      key: subChapterKey,
                      name: subChapterName,
                      level,
                      data: subChapterData,
                    }) => {
                      const getColorByLevel = (level) => {
                        const l = level?.toLowerCase?.();
                        if (l === "high") return "#F28C94";
                        if (l === "medium") return "#696969";
                        if (l === "low") return "#7ADDAF";
                        return "#696969"; // fallback grey
                      };

                      const allFindings = subChapterData
                        .flatMap((d) => d.relatedFindings || [])
                        .filter((f) => !!f);

                      const findingsMap = new Map();

                      allFindings.forEach((f) => {
                        if (!f || findingsMap.has(f.key)) return;

                        const isResolved =
                          getLatestStatus(f, months[months.length - 1]) ===
                            "Done" &&
                          (f.linked_improvements || []).every(
                            (imprKey) =>
                              improvementsData?.[imprKey]?.status === "Done",
                          );

                        findingsMap.set(f.key, { ...f, isResolved });
                      });

                      const uniqueFindings = Array.from(
                        findingsMap.values(),
                      ).sort((a, b) => {
                        return a.isResolved - b.isResolved; // unresolved first (false < true)
                      });

                      const allImprovements = subChapterData
                        .flatMap((d) => d.relatedImprovements || [])
                        .filter((i) => !!i);

                      const improvementMap = new Map();

                      allImprovements.forEach((impr) => {
                        if (!impr || improvementMap.has(impr.key)) return;

                        const isResolved = impr.status === "Done";
                        improvementMap.set(impr.key, { ...impr, isResolved });
                      });

                      const uniqueImprovements = Array.from(
                        improvementMap.values(),
                      ).sort(
                        (a, b) => a.isResolved - b.isResolved, // false (not done) before true (done)
                      );

                      return (
                        <div key={subChapterKey} className={styles.graph}>
                          <div className={styles.subChapterHeader}>
                            <div className={styles.subChapterKey}>
                              <span className={styles.subChapterPill}>
                                {subChapterKey}
                              </span>
                            </div>
                            <span className={styles.subChapterName}>
                              {truncateText(subChapterName, 30) ?? ""}
                            </span>
                            <span
                              className={classNames(styles.levelPill, {
                                [styles.low]: level === "low",
                                [styles.medium]: level === "medium",
                                [styles.high]: level === "high",
                              })}
                            >
                              {level
                                ? level.charAt(0).toUpperCase() +
                                  level.slice(1).toLowerCase()
                                : ""}
                            </span>
                            <span
                              className={classNames(
                                styles.count,
                                styles.auditCount,
                              )}
                            >
                              {auditCountBySubchapter[subChapterKey] || 0}
                            </span>
                            <span
                              className={classNames(
                                styles.count,
                                styles.findingCount,
                              )}
                            >
                              {findingCountBySubchapter[subChapterKey] || 0}
                            </span>
                            <span
                              className={classNames(
                                styles.count,
                                styles.improvementCount,
                              )}
                            >
                              {improvementCountBySubchapter[subChapterKey] || 0}
                            </span>
                            <span
                              className={classNames(
                                styles.count,
                                styles.exposureCount,
                              )}
                            >
                              <span className={styles.exposurePill}>
                                {exposureBySubchapter[subChapterKey] || 0}
                              </span>
                            </span>
                            <span
                              className={classNames(
                                styles.count,
                                styles.rpsCount,
                              )}
                            >
                              <span className={styles.rpsPill}>
                                {rpsBySubchapter[subChapterKey] || 0}
                              </span>
                            </span>
                          </div>
                          <div className={styles.graphWrapper}>
                            <LineGraph
                              data={subChapterData.map((item, index) => ({
                                month: months[index],
                                count: item.count,
                                relatedFindings: item.relatedFindings || [],
                                relatedImprovements: dedupeImprovements(
                                  item.relatedImprovements || [],
                                ),
                              }))}
                              months={months}
                            />
                            <div className={styles.currentItems}>
                              <div className={styles.currentRow}>
                                <span className={styles.bold}>
                                  Current findings:
                                </span>
                                {uniqueFindings.length === 0 ? (
                                  <span style={{ color: "#999" }}>
                                    There are none at the moment.
                                  </span>
                                ) : (
                                  uniqueFindings
                                    .slice(0, 6)
                                    .map((finding, i) => (
                                      <span key={i}>
                                        <span
                                          style={{
                                            color: getColorByLevel(
                                              finding?.risk?.level,
                                            ),
                                            fontWeight: 500,
                                          }}
                                        >
                                          <a
                                            href={`/${orgId}/findings/${finding.key}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              color: getColorByLevel(
                                                finding?.risk?.level,
                                              ),
                                              fontWeight: 500,
                                            }}
                                          >
                                            {finding.key}:
                                          </a>
                                        </span>{" "}
                                        {truncateText(finding.description, 50)}
                                        {i <
                                          Math.min(
                                            5,
                                            uniqueFindings.length - 1,
                                          ) && ", "}
                                      </span>
                                    ))
                                )}
                                {uniqueFindings.length > 0 && (
                                  <Button
                                    text="See related findings"
                                    className={styles.linkButton}
                                    icon={<RightArrowIcon />}
                                    iconPosition="right"
                                    onClick={() =>
                                      navigate(
                                        `/${orgId}/findings?risk=${subChapterKey}`,
                                      )
                                    }
                                  />
                                )}
                              </div>

                              <div className={styles.currentRow}>
                                <span className={styles.bold}>
                                  Current improvements:
                                </span>
                                {uniqueImprovements.length === 0 ? (
                                  <span style={{ color: "#999" }}>
                                    There are none at the moment.
                                  </span>
                                ) : (
                                  uniqueImprovements
                                    .slice(0, 6)
                                    .map((impr, i) => (
                                      <span key={i}>
                                        <span
                                          style={{
                                            color: getColorByLevel(
                                              getImprovementRiskLevelForSubchapter(
                                                impr,
                                                subChapterKey,
                                                findingsData,
                                              ),
                                            ),
                                            fontWeight: 500,
                                          }}
                                        >
                                          <a
                                            href={`/${orgId}/improvement-projects/${impr.key}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              color: getColorByLevel(
                                                getImprovementRiskLevelForSubchapter(
                                                  impr,
                                                  subChapterKey,
                                                  findingsData,
                                                ),
                                              ),
                                              fontWeight: 500,
                                            }}
                                          >
                                            {impr.key}:
                                          </a>
                                        </span>{" "}
                                        {truncateText(impr.description, 50)}
                                        {i <
                                          Math.min(
                                            5,
                                            uniqueImprovements.length - 1,
                                          ) && ", "}
                                      </span>
                                    ))
                                )}
                                {uniqueImprovements.length > 0 && (
                                  <Button
                                    text="See related improvements"
                                    className={styles.linkButton}
                                    icon={<RightArrowIcon />}
                                    iconPosition="right"
                                    onClick={() =>
                                      navigate(
                                        `/${orgId}/improvement-projects?risk=${subChapterKey}`,
                                      )
                                    }
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    },
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
      <Tooltip id="month-tooltip" place="top" className={styles.tooltip} />
      <Tooltip
        id="exposure-tooltip"
        place="top"
        content="EXPOSURE shows to what risk the organization has been dealing with the most in total. All the findings x The impact levels."
        className={classNames(styles.tooltip, styles.headerTooltip)}
      />
      <Tooltip
        id="rps-tooltip"
        place="top"
        content="PRIORITY SCORE indicates which chapter/risk has the most priority to be looked at. EXPOSURE - How many of those are resolved using improvement projects."
        className={classNames(styles.tooltip, styles.headerTooltip)}
      />
    </div>
  );
};

export default GraphAccordion;
